import {use, useSyncExternalStore} from 'react'
import type {EstablishedAhpSession, EstablishedAhpSessionViewSnapshot} from '@github-ui/ahp-session-client/contracts'

import {EstablishedAhpSessionSnapshotContext} from '../contexts/EstablishedAhpSessionContext'
import {useEstablishedAhpSession} from './use-established-ahp-session'

const EMPTY_VIEW_SNAPSHOT: EstablishedAhpSessionViewSnapshot = {
  state: undefined,
  status: 'idle',
  reachable: false,
  error: undefined,
  modelFailures: undefined,
  provisioning: undefined,
  sessionState: undefined,
  passive: false,
  passiveKnown: false,
  steeringMessages: [],
}

export interface EstablishedAhpSessionViewState {
  readonly snapshot: EstablishedAhpSessionViewSnapshot
  readonly state: EstablishedAhpSessionViewSnapshot['state']
  readonly status: EstablishedAhpSessionViewSnapshot['status']
  readonly reachable: EstablishedAhpSessionViewSnapshot['reachable']
  readonly error: EstablishedAhpSessionViewSnapshot['error']
  readonly modelFailures: EstablishedAhpSessionViewSnapshot['modelFailures']
  readonly provisioning: EstablishedAhpSessionViewSnapshot['provisioning']
  readonly sessionState: EstablishedAhpSessionViewSnapshot['sessionState']
  readonly passive: EstablishedAhpSessionViewSnapshot['passive']
  readonly passiveKnown: EstablishedAhpSessionViewSnapshot['passiveKnown']
  readonly steeringMessages: EstablishedAhpSessionViewSnapshot['steeringMessages']
}

export function useOptionalEstablishedAhpSessionViewState(): EstablishedAhpSessionViewState {
  const snapshot = use(EstablishedAhpSessionSnapshotContext) ?? EMPTY_VIEW_SNAPSHOT
  return {...snapshot, snapshot}
}

export function useEstablishedAhpSessionViewState(): EstablishedAhpSessionViewState {
  const session = useEstablishedAhpSession()
  const state = useOptionalEstablishedAhpSessionViewState()
  if (!session) throw new Error('useEstablishedAhpSessionViewState must be used within EstablishedAhpSessionProvider')
  return state
}

export function useEstablishedAhpSessionLifecycleState(): ReturnType<EstablishedAhpSession['lifecycle']['getState']> {
  const lifecycle = useEstablishedAhpSession().lifecycle
  return useSyncExternalStore(lifecycle.subscribe, lifecycle.getState, () => 'connecting' as const)
}
